<script setup lang="ts">
//#region imports
import { h, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useApi } from '@/api';
import Flag from '@/components/Flag.vue';
import UiIcon from '@/components/UIElements/UiIcon.vue';
import { useTablePaginator } from '@/composables/useTableLS';
import { getCountryCodeByLanguage } from '@/helpers/language';
import CrmTag from '@/views/CRM/components/CrmTag.vue';
import type { UiTableNewField } from '@/types/table';
import type { GetPlayersByIdCrmLogsResponse } from '@/types/openapi-ts';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region types
type PlayerCrmLog = GetPlayersByIdCrmLogsResponse['payload'][number];
type CrmActionType = 'email' | 'sms' | 'call' | 'push';
type CrmTagPreset = `notify-${CrmActionType}`;
//#endregion

//#region define

//#endregion

//#region const
const LS_PATH = '/players/crm';

const NOTIFY_PRESETS = {
  email: 'notify-email',
  sms: 'notify-sms',
  call: 'notify-call',
  push: 'notify-push',
} as const satisfies Record<CrmActionType, CrmTagPreset>;

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<PlayerCrmLog>[] = [
  {
    name: 'event_timestamp',
    width: '150',
    format: 'date',
    formatOptions: {
      in: '',
      out: 'dd MMM yyyy HH:mm:ss',
    },
  },
  {
    name: 'activity_name',
  },
  {
    name: 'language',
    label: 'language',
    align: 'center',
    linkAppend: renderLanguage,
    width: '90',
    headerAlign: 'center',
  },
  {
    name: 'type',
    linkAppend: renderActions,
    align: 'center',
    width: '70',
    headerAlign: 'center',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
//#endregion

//#region state
const isDataLoading = ref(false);
const tableData = ref<GetPlayersByIdCrmLogsResponse['payload']>([]);
//#endregion

//#region methods
async function fetchData() {
  isDataLoading.value = true;
  try {
    const { data: { payload, misc } } = await api.getPlayerLogs(route.params.id as string, {
      limit: limit.value,
      offset: (page.value - 1) * limit.value,
    });

    count.value = misc?.count || 0;
    tableData.value = payload;
  } finally {
    isDataLoading.value = false;
  }
}

function renderLanguage(row: PlayerCrmLog) {
  const countryCode = getCountryCodeByLanguage(row.action_language_code);

  const finalLanguages = [
    h(CrmTag, { preset: 'gray' }, {
      prepend: () => countryCode
        ? h(Flag, { iso: countryCode })
        : h(UiIcon, { name: 'globe' }),
      default: () => h(
        'span',
        { class: 't:capitalize' },
        row.action_language_code,
      ),
    }),
  ];

  return h('div', { class: 't:flex t:gap-4' }, finalLanguages);
}

function renderActions(row: PlayerCrmLog) {
  const finalActions = [
    h(CrmTag, { preset: NOTIFY_PRESETS[row.action_type as CrmActionType] }),
  ];
  return h('div', { class: 't:flex t:gap-4' }, finalActions);
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};
//#endregion

//#region watch
watch(() => route.params.id, () => {
  fetchData();
});
//#endregion

//#region hooks
fetchData();
//#endregion
</script>

<template>
  <UiToolbar>
    <template v-slot:title>
      {{ $t('players.crm.title') }}
    </template>
    <template v-slot:right-slot>
      <UiPagination
        :page="page"
        :page-size="limit"
        :count="count"
        show-size-select
        @page-change="pageChange"
      />
    </template>
  </UiToolbar>
  <UiTableNew
    v-loading="isDataLoading"
    :lazy-loading-delay="4000"
    is-static
    :fields="fields"
    :data="tableData"
    :rows-count="limit"
    lazy-loading
    disable-sort
    i18n-path="players.crm"
    element-loading-custom-class="data-loader-spinner"
  >
    <template v-slot:append>
      <ElTableColumn
        :resizable="false"
        width="78"
        align="center"
        header-align="center"
      >
        <template v-slot:header="scope">
          <div
            :key="scope.$index"
            class="table-action-header"
          >
            {{ $t('settings.tokens.actions') }}
          </div>
        </template>
        <template v-slot="scope">
          <ActionIcon
            icon="eye"
            :tooltip="$t('crm.buttons.view')"
            :router-to="`/crm/activity/${scope.row.activity_id}`"
          />
        </template>
      </ElTableColumn>
    </template>
  </UiTableNew>
</template>
